/**
 * WebSocket session hook: connects, joins, auto-reconnects with backoff
 * (N-3), and exposes the latest authoritative state plus a send function.
 * Clock smoothing between server broadcasts lives in useDisplayClock.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientMessage, Role, ServerMessage } from '../../shared/protocol';
import type { PublicState } from '../../shared/types';

export type SessionStatus = 'connecting' | 'ready' | 'error';

export interface SessionHandle {
  state: PublicState | null;
  status: SessionStatus;
  fatalError: string | null;
  /** Transient rejection (e.g. „Nicht am Zug“) — clears itself. */
  denied: string | null;
  authorIndex: number | null;
  /** serverNow - Date.now() at last message; aligns joker deadlines. */
  clockOffset: number;
  /** Date.now() when the latest state arrived (for clock interpolation). */
  stateReceivedAt: number;
  send: (msg: ClientMessage) => void;
}

function wsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/ws`;
}

export function useSession(
  pin: string,
  role: Role,
  opts: {
    token?: string | null;
    name?: string | null;
    /** Called once with the (new or confirmed) token, for localStorage. */
    onToken?: (token: string, authorIndex: number | null) => void;
    /** Author view: pause connecting until a name or token exists. */
    enabled?: boolean;
  },
): SessionHandle {
  const [state, setState] = useState<PublicState | null>(null);
  const [status, setStatus] = useState<SessionStatus>('connecting');
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [denied, setDenied] = useState<string | null>(null);
  const [authorIndex, setAuthorIndex] = useState<number | null>(null);
  const [clockOffset, setClockOffset] = useState(0);
  const [stateReceivedAt, setStateReceivedAt] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const deniedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const enabled = opts.enabled !== false;
  const token = opts.token ?? null;
  const name = opts.name ?? null;

  useEffect(() => {
    if (!enabled) return;
    let closed = false;
    let retryDelay = 500;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    let pingTimer: ReturnType<typeof setInterval> | null = null;

    const connect = () => {
      if (closed) return;
      setStatus('connecting');
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        retryDelay = 500;
        const hello: ClientMessage = {
          type: 'hello',
          pin,
          role,
          ...(token ? { token } : {}),
          ...(name ? { name } : {}),
        };
        ws.send(JSON.stringify(hello));
        // Keep-alive for hosted deployments: platform proxies hang up idle
        // sockets. A ping every 30 s keeps the line open (and re-syncs the
        // clock offset via the pong). Harmless on a venue LAN.
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          if (ws.readyState === ws.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
        }, 30_000);
      };

      ws.onmessage = (ev) => {
        const msg = JSON.parse(String(ev.data)) as ServerMessage;
        switch (msg.type) {
          case 'welcome':
            setAuthorIndex(msg.authorIndex);
            setState(msg.state);
            setClockOffset(msg.serverNow - Date.now());
            setStateReceivedAt(Date.now());
            setStatus('ready');
            setFatalError(null);
            if (msg.token) optsRef.current.onToken?.(msg.token, msg.authorIndex);
            break;
          case 'state':
            setState(msg.state);
            setClockOffset(msg.serverNow - Date.now());
            setStateReceivedAt(Date.now());
            break;
          case 'denied':
            setDenied(msg.reason);
            if (deniedTimer.current) clearTimeout(deniedTimer.current);
            deniedTimer.current = setTimeout(() => setDenied(null), 2500);
            break;
          case 'error':
            if (msg.fatal) {
              setFatalError(msg.message);
              setStatus('error');
              closed = true;
              ws.close();
            } else {
              setDenied(msg.message);
            }
            break;
          case 'pong':
            setClockOffset(msg.serverNow - Date.now());
            break;
        }
      };

      ws.onclose = () => {
        if (pingTimer) {
          clearInterval(pingTimer);
          pingTimer = null;
        }
        if (closed) return;
        retryTimer = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 5000);
        setStatus('connecting');
      };
      ws.onerror = () => ws.close();
    };

    connect();
    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (pingTimer) clearInterval(pingTimer);
      wsRef.current?.close();
    };
  }, [pin, role, token, name, enabled]);

  const send = useCallback((msg: ClientMessage) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === ws.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  return { state, status, fatalError, denied, authorIndex, clockOffset, stateReceivedAt, send };
}

/** Re-render pulse for smooth countdowns between server broadcasts. */
export function useTicker(intervalMs = 200): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Displayed remaining ms for a seat, interpolated since the last broadcast. */
export function remainingNow(
  state: PublicState,
  stateReceivedAt: number,
  now: number,
  seat: number,
): number {
  const r = state.round;
  const base = r.remainingMs[seat] ?? 0;
  const running = r.phase === 'running' && r.activeAuthor === seat && !r.frozen[seat];
  if (!running) return base;
  return Math.max(0, base - (now - stateReceivedAt));
}
